/**
 * Application Insights Configuration
 * Captures client-side errors, page views, and performance metrics
 */

import { ApplicationInsights } from "@microsoft/applicationinsights-web";

let appInsights: ApplicationInsights | null = null;

/**
 * Initialize Application Insights
 * Call this once at app startup
 */
export function initAppInsights(): ApplicationInsights | null {
  const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING;

  if (!connectionString) {
    console.log(
      "%c[App Insights] Not configured - skipping initialization",
      "color: orange",
    );
    return null;
  }

  console.log("%c[App Insights] Initializing...", "color: #0078d4");

  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      // Automatically track page views
      enableAutoRouteTracking: true,
      // Add network timing breakdown to browser-side API dependency telemetry.
      enableAjaxPerfTracking: true,
      // Track unhandled exceptions
      enableUnhandledPromiseRejectionTracking: true,
      // Enable telemetry in all environments for now (set to false to disable in dev)
      disableTelemetry: false,
      // Sample rate (1 = 100%, 0.1 = 10%)
      samplingPercentage: 100,
      // Enable console logging for debugging
      loggingLevelConsole: import.meta.env.DEV ? 2 : 0,
      // Disable deprecated unload event listeners (fixes console warning)
      disablePageUnloadEvents: ["unload"],
    },
  });

  appInsights.loadAppInsights();

  // Set cloud role name to distinguish frontend from backend
  appInsights.addTelemetryInitializer((item) => {
    if (item.tags) {
      item.tags["ai.cloud.role"] = "SeaSlot-Frontend";
    }
  });

  console.log(
    "%c[App Insights] ✓ Initialized successfully",
    "color: #107c10; font-weight: bold",
  );
  return appInsights;
}

/**
 * Get the App Insights instance
 */
export function getAppInsights(): ApplicationInsights | null {
  return appInsights;
}

/**
 * Track a custom event
 */
export function trackEvent(name: string, properties?: Record<string, string>) {
  appInsights?.trackEvent({ name }, properties);
}

/**
 * Track an exception/error
 */
export function trackException(
  error: Error,
  properties?: Record<string, string>,
) {
  appInsights?.trackException({ exception: error }, properties);
}

/**
 * Track a page view (manual)
 */
export function trackPageView(name: string, uri?: string) {
  appInsights?.trackPageView({ name, uri });
}

/**
 * Set authenticated user context
 */
export function setAuthenticatedUser(userId: string, accountId?: string) {
  appInsights?.setAuthenticatedUserContext(userId, accountId, true);
}

/**
 * Clear authenticated user context (on logout)
 */
export function clearAuthenticatedUser() {
  appInsights?.clearAuthenticatedUserContext();
}
